/**
 * Truck Status for the main board.
 * Locked codes match BoardTruckStatusResolver on the server.
 * Appointment windows use the same generateTimestamp helper as the board's open-truck check.
 */
import { generateTimestamp } from '../general_components/content_management/task_list/taskListElements/diagramView';

const CUSTOM_LABELS = {
    yard: 'YARD',
    park: 'RELOCATE',
    custom: 'CUSTOM_TASK',
    puTrailer: 'PICKUP_TRAILER',
    dropTrailer: 'DROP_TRAILER',
    swapTrailer: 'SWAP_TRAILERS',
};

const MANUAL_CODES = new Set(['OPEN', 'COVERED', 'UPCOMING_LOAD', 'SHOP', 'AT_HOME', 'PENDING']);

const MANUAL_RANK = {
    OPEN: 0,
    SHOP: 1,
    PENDING: 2,
    UPCOMING_LOAD: 3,
    COVERED: 4,
};

export const MANUAL_STATUS_OPTIONS = [
    { code: 'OPEN', label: 'Open' },
    { code: 'COVERED', label: 'Covered' },
    { code: 'UPCOMING_LOAD', label: 'Upcoming Load' },
    { code: 'SHOP', label: 'Shop' },
    { code: 'AT_HOME', label: 'At Home' },
    { code: 'PENDING', label: 'Pending' },
];

export const TRUCK_STATUS_LABELS = {
    OPEN: 'Open',
    COVERED: 'Covered',
    UPCOMING_LOAD: 'Upcoming Load',
    DISPATCHED: 'Dispatched',
    SHOP: 'Shop',
    AT_HOME: 'At Home',
    PENDING: 'Pending',
    RESET: 'Reset',
    YARD: 'Yard',
    RELOCATE: 'Relocate',
    CUSTOM_TASK: 'Custom Task',
    PICKUP_TRAILER: 'Pickup Trailer',
    DROP_TRAILER: 'Drop Trailer',
    SWAP_TRAILERS: 'Swap Trailers',
};

export function truckStatusLabel(code) {
    return TRUCK_STATUS_LABELS[code] || 'Open';
}

/** Softer text colors for the open-status menu. The badge keeps the stronger palette. */
const MENU_TEXT = {
    OPEN: { light: '#C46B6B', dark: '#E09898' },
    COVERED: { light: '#5A9E82', dark: '#86C8AC' },
    UPCOMING_LOAD: { light: '#5E92BE', dark: '#96C0DE' },
    SHOP: { light: '#C4963E', dark: '#E2C078' },
    AT_HOME: { light: '#7E68A8', dark: '#C4A8DC' },
    PENDING: { light: '#A87BC0', dark: '#D4B4E4' },
};

export function truckStatusMenuColor(code, dark) {
    const pair = MENU_TEXT[code] || { light: 'var(--gray)', dark: 'var(--light-gray)' };
    return dark ? pair.dark : pair.light;
}

export function truckStatusStyle(code) {
    switch (code) {
        case 'OPEN':
            return { background: 'rgba(var(--lightAlertRGB), 0.16)', color: 'rgb(var(--lightAlertRGB))' };
        case 'COVERED':
            return { background: 'rgba(48, 192, 140, 0.18)', color: 'var(--main-color)' };
        case 'UPCOMING_LOAD':
            return { background: 'rgba(var(--linkColorRGB), 0.18)', color: 'rgb(var(--linkColorRGB))' };
        case 'DISPATCHED':
        case 'PICKUP_TRAILER':
        case 'DROP_TRAILER':
        case 'SWAP_TRAILERS':
            return { background: 'rgba(var(--inProgressBlueRGB), 0.16)', color: 'rgb(var(--inProgressBlueRGB))' };
        case 'SHOP':
        case 'YARD':
        case 'RELOCATE':
            return { background: 'rgba(var(--inProgressYellowRGB), 0.22)', color: 'rgb(var(--inProgressYellowRGB))' };
        case 'AT_HOME':
            return {
                background: 'rgba(74, 29, 122, 0.16)',
                color: '#4A1D7A',
                darkBackground: 'rgba(74, 29, 122, 0.55)',
                darkColor: '#e4c6ff',
            };
        case 'PENDING':
            return {
                background: 'rgba(220, 177, 250, 0.5)',
                color: '#7A3EAA',
                darkBackground: 'rgba(220, 177, 250, 0.22)',
                darkColor: '#e4c6ff',
            };
        case 'RESET':
        case 'CUSTOM_TASK':
            return { background: 'rgba(var(--inProgressPurpleRGB), 0.18)', color: 'rgb(var(--inProgressPurpleRGB))' };
        default:
            return { background: 'var(--extra-light-gray)', color: 'var(--gray)' };
    }
}

export function normalizeManualStatus(value) {
    if (value == null || value === '') return 'OPEN';
    const code = String(value).trim().toUpperCase();
    return MANUAL_CODES.has(code) ? code : 'OPEN';
}

/** Null when the truck is still open and the dispatcher may choose a status. */
export function lockedCode(tasks, nowMs) {
    if (!Array.isArray(tasks) || tasks.length === 0 || nowMs == null) return null;
    let checking = true;
    const timeSpecified = [];
    for (let i = 0; i < tasks.length && checking; i++) {
        const task = tasks[i];
        if (!task) continue;
        if (task.loadId != null) {
            const outcome = loadOutcome(task, nowMs);
            if (outcome.locked) return outcome.locked;
            if (outcome.stopOpen) checking = false;
            continue;
        }
        const action = firstOpenCustomAction(actionList(task));
        if (!action) {
            rememberTimeSpecified(task, timeSpecified);
            continue;
        }
        const outcome = customOutcome(task, action, i, nowMs, timeSpecified);
        if (outcome.locked) return outcome.locked;
        if (outcome.stopOpen) {
            checking = false;
            continue;
        }
        rememberTimeSpecified(task, timeSpecified);
    }
    return null;
}

export function displayedTruckStatus(truck, nowMs) {
    const locked = lockedCode(truck?.tasks, nowMs);
    if (locked) return { code: locked, editable: false };
    return { code: normalizeManualStatus(truck?.manualBoardStatus), editable: true };
}

/**
 * Open, Shop, Pending, Upcoming Load, then Covered with no load.
 * Trucks locked by a real load or task stay in delivery-time order under that band.
 * Manual At Home is always last. At Home from a home-time task uses delivery/availability time.
 */
export function compareBoardTrucks(a, b, nowMs) {
    const pa = placement(a, nowMs);
    const pb = placement(b, nowMs);
    if (pa.band !== pb.band) return pa.band - pb.band;
    if (pa.band === 0 && pa.rank !== pb.rank) return pa.rank - pb.rank;
    if (pa.band === 1 && pa.time !== pb.time) return pa.time - pb.time;
    return truckLabel(a).localeCompare(truckLabel(b), undefined, { numeric: true });
}

function placement(truck, nowMs) {
    if (lockedCode(truck?.tasks, nowMs)) {
        const time = truck?.lastLoadsLastTimecode;
        return { band: 1, rank: 0, time: Number.isFinite(time) ? time : 0 };
    }
    const manual = normalizeManualStatus(truck?.manualBoardStatus);
    if (manual === 'AT_HOME') return { band: 2, rank: 0, time: 0 };
    return { band: 0, rank: MANUAL_RANK[manual] ?? 0, time: 0 };
}

function truckLabel(truck) {
    if (truck?.truckNumber != null) return String(truck.truckNumber);
    if (truck?.id != null) return String(truck.id);
    return '';
}

function loadOutcome(task, nowMs) {
    const stopIndex = Number.isFinite(Number(task.loadStopIndex)) ? Number(task.loadStopIndex) : 0;
    const actions = actionList(task);
    const loadAction = firstLoadAction(actions);
    const completed = loadAction != null && bool(loadAction.completed);
    if (stopIndex > 0 || bool(task.onSite) || completed) return lock('COVERED');
    const appt = apptMs(task, false);
    if (appt != null && within(nowMs, appt, 48)) {
        return lock(loadAction != null && bool(loadAction.dispatched) ? 'DISPATCHED' : 'UPCOMING_LOAD');
    }
    return stopOpen();
}

function customOutcome(task, action, index, nowMs, timeSpecified) {
    const type = action?.type;
    if (type === 'homeTime' || type === 'reset') {
        if (index === 0 && bool(task.onSite)) {
            const start = apptMs(task, type === 'reset');
            if (start != null && within(nowMs, start, 24)) {
                return lock(type === 'homeTime' ? 'AT_HOME' : 'RESET');
            }
        }
        return cont();
    }
    if (type === 'shop') {
        if (task.apptDate) {
            const start = apptMs(task, true);
            if (start != null && within(nowMs, start, 24)) return lock('SHOP');
            return stopOpen();
        }
        if (timeSpecified.length === 0) return lock('SHOP');
        const start = apptMs(timeSpecified[timeSpecified.length - 1], true);
        if (start != null && within(nowMs, start, 24)) return lock('SHOP');
        return stopOpen();
    }
    const label = CUSTOM_LABELS[type];
    if (!label) return cont();
    const noDate = !task.apptDate;
    const start = noDate ? null : apptMs(task, false);
    if (bool(task.onSite) || noDate || (start != null && within(nowMs, start, 24))) {
        return lock(label);
    }
    return cont();
}

function rememberTimeSpecified(task, timeSpecified) {
    if (task?.apptDate && task?.apptTime) timeSpecified.push(task);
}

function firstLoadAction(actions) {
    let fallback = null;
    for (const action of actions) {
        if (!fallback) fallback = action;
        if (bool(action?.loadAction)) return action;
    }
    return fallback;
}

function firstOpenCustomAction(actions) {
    for (const action of actions) {
        if (bool(action?.loadAction) || bool(action?.completed)) continue;
        return action;
    }
    return null;
}

function actionList(task) {
    let actions = task?.actions;
    if (typeof actions === 'string') {
        try {
            actions = JSON.parse(actions);
        } catch (e) {
            return [];
        }
    }
    return Array.isArray(actions) ? actions : [];
}

function apptMs(task, forceCompanyZone) {
    if (!task?.apptDate) return null;
    let time = task.apptTime;
    if (time == null || String(time).trim() === '') {
        time = '12:00 AM';
    } else if (String(time).trim().startsWith('00:')) {
        // Match the server: "00:00 AM" is midnight, which Java's clock-hour parser rejects.
        time = `12:${String(time).trim().substring(3)}`;
    }
    const ts = generateTimestamp(
        task.apptDate,
        time,
        forceCompanyZone ? null : task.address,
        false,
        null,
        !!forceCompanyZone
    );
    const ms = ts && typeof ts === 'object' ? ts.startTime : ts;
    return Number.isFinite(ms) ? ms : null;
}

function within(nowMs, apptMsValue, hours) {
    if (nowMs == null || apptMsValue == null) return false;
    return nowMs + hours * 60 * 60 * 1000 >= apptMsValue;
}

function bool(value) {
    return value === true || value === 'true' || value === 1 || value === '1';
}

function lock(code) {
    return { locked: code, stopOpen: false };
}
function stopOpen() {
    return { locked: null, stopOpen: true };
}
function cont() {
    return { locked: null, stopOpen: false };
}
